// Copyright 2025-2026 - Hewlett Packard Enterprise Company

// When adding new routes, ensure that longer matches are inserted in front of shorter matches
// For example, /analytics/carbonFootprint should come before /analytics
// The order matters, or else you'll see weird bugs.

/*
 * Route config - helpTopic, exact, ldFlags, etc.
 *
 * helpTopic - the support topic index to display in contextual help for this page
 * ldFlags - list of LD flags to || together. If you need more complex logic, set
 *   the complexLDFlag attribute to true and manually add the route to Routes and
 *   the help topic resolution to Help
 * complexLDFlag - for more complex scenarios where the route shouldn't be automatically
 *   added to the routes. When cleaning up the LD flags, remove this attribute
 *   YOU MUST MANUALLY ADD your route to Routes.js and topic resolution to Help.js
 *   There are examples of how to do this inline in both files.
 * mspSupported - set to true for the handful of routes an MSP workspace can access
 *   without a tenant selected (isTenantView === false). Any authenticated route
 *   without this flag redirects an MSP-without-tenant to the MSP overview.
 *   AuthenticatedRoute enforces this; it is not passed through to the Route.
 * All other attributes are passed through to the Route component as is.
 */

// temporarily adding a default topic for routes to use that didn't configure one
// new routes should work with Susan to get an appropriate topic ID
const DEFAULT_TOPIC = 'com-404-tt';

export const ROUTE_URLS = {
  ACTIVITY: '/activity',
  AHS_ANALYZER: '/ahsAnalyzer',
  AHS_ANALYZER_DETAIL: '/ahsAnalyzerDetails/:id',
  AHS_LOG_ANALYZER: '/ahsLogAnalyzer',
  CARBON_FOOTPRINT_ANALYSIS: '/analytics/carbonFootprint',
  CARBON_FOOTPRINT_DETAIL: '/analytics/carbonFootprintDetail/:id',
  SERVER_UTILIZATION_DETAIL: '/analytics/serverUtilizationDetail/:id',
  SERVER_UTILIZATION_ANALYSIS: '/analytics/serverUtilization',
  ANALYTICS: '/analytics',
  APPLIANCE_DETAIL: '/applianceDetail/:id',
  APPLIANCES: '/appliances',
  APPROVAL_POLICY: '/approvalPolicy',
  APPROVAL_REQUESTS: '/approvalRequests',
  ARUBA_CENTRAL: '/arubaCentral',
  DSCC_INTEGRATION: '/dsccIntegration',
  EMAIL_NOTIFICATION: '/emailNotification',
  ERROR: '/error/:code',
  FIRMWARE_ANALYSIS_SERVER_REPORT_ALL:
    '/firmwareAnalysisReport/server/:serverId',
  FIRMWARE_ANALYSIS_SERVER_REPORT:
    '/firmwareAnalysisReport/:id/server/:serverId',
  FIRMWARE_ANALYSIS_REPORT: '/firmwareAnalysisReport/:id',
  FIRMWARE_ANALYSIS_REPORT_ALL: '/firmwareAnalysisReport',
  FIRMWARE_UPDATE_REPORT: '/firmwareUpdateReport/:id',
  DEVICE_FIRMWARE_ANALYSIS_REPORT: '/deviceFirmwareAnalysisReport/:id',
  DOWNLOAD_FIRMWARE_REPORT: '/downloadFirmwareReport/:id',
  FIRMWARE: '/firmware',
  SERVERS_FIRMWARE: '/servers/firmware',
  GROUP_COMPLIANCE_ISSUE: '/groupComplianceIssue/:id',
  GROUP_COMPLIANCE_ISSUES: '/groupComplianceIssues/:id',
  GROUP_DETAIL: '/groupDetail/:id',
  GROUP_EXTERNAL_STORAGE_COMPLIANCE: '/groupExternalStorageCompliance/:id',
  GROUP_ILO_DETAIL_ISSUE: '/groupIloDetailIssue/:id',
  GROUPS: '/groups',
  HARDWARE_DETAIL: '/hardwareDetail/:applianceId/:hardwareId',
  INVENTORY: '/inventory',
  MANAGE: '/manage',
  METRICS_DATA: '/metricsData',
  OVERVIEW: '/overview',
  ALL_REGIONS_OVERVIEW: '/all-regions/overview', // for non-MSP account
  ALL_REGIONS_TENANTS: '/all-regions/tenants', // for MSP account
  POLICY_DETAIL: '/policyDetail/:id',
  REPORTS_CARBON_FOOTPRINT_DETAIL: '/reports/carbonFootprint/:id',
  REPORTS_SERVER_HARDWARE_INVENTORY_DETAIL:
    '/reports/serverHardwareInventory/:id',
  REPORTS_SERVER_HARDWARE_INVENTORY: '/reports/serverHardwareInventory',
  REPORTS: '/reports',
  SERVER_DETAIL: '/serverDetail/:id',
  SERVER_PROFILE_DETAIL: '/serverProfileDetail/:applianceId/:serverProfileId',
  SERVER_PROFILE: '/serverProfile',
  SERVERS: '/servers',
  SERVICENOW: '/serviceNow',
  SETTING_DETAIL: '/settingDetail/:id',
  SETTINGS: '/settings',
  VCENTER_DETAIL: '/vcenterDetail/:id',
  VSPHERE_INTEGRATION: '/vsphereIntegration',
  HARDWARE_SECURITY_RISK_DETAILS:
    '/hardwareSecurityRiskDetails/:groupId/device/:deviceId',
  GROUP_HARDWARE_SECURITY_RISK: '/groupHardwareSecurityRisk/:id',
};

const ROUTE_CONFIG = {
  [ROUTE_URLS.ACTIVITY]: { helpTopic: 'com-activity-tt', mspSupported: true },
  [ROUTE_URLS.AHS_ANALYZER]: {
    helpTopic: DEFAULT_TOPIC,
    ldFlags: ['P_20260320_4980_HPE_AHS_SUPPORT_WORKSPACE'],
    exact: true,
    isExact: true,
    isSessionStorageEnabledForPage: true,
  },
  [ROUTE_URLS.AHS_ANALYZER_DETAIL]: {
    helpTopic: DEFAULT_TOPIC,
    ldFlags: ['P_20260825_4980_HPE_AHS_SUPPORT_WORKSPACE_PHASE2'],
  },
  [ROUTE_URLS.AHS_LOG_ANALYZER]: { helpTopic: 'com-ahs-analyzer-tt' },
  [ROUTE_URLS.CARBON_FOOTPRINT_ANALYSIS]: {
    helpTopic: 'com-sustainability-tt',
    isSessionStorageEnabledForPage: true,
  },
  [ROUTE_URLS.CARBON_FOOTPRINT_DETAIL]: {
    helpTopic: 'com-sustainability-tt',
    isSessionStorageEnabledForPage: true,
  },
  [ROUTE_URLS.SERVER_UTILIZATION_DETAIL]: {
    helpTopic: 'com-server-utilization-tt',
    isSessionStorageEnabledForPage: true,
  },
  [ROUTE_URLS.SERVER_UTILIZATION_ANALYSIS]: {
    helpTopic: 'com-server-utilization-tt',
    isSessionStorageEnabledForPage: true,
  },
  [ROUTE_URLS.ANALYTICS]: { helpTopic: 'com-analytics-tt' },
  [ROUTE_URLS.APPLIANCE_DETAIL]: { helpTopic: 'com-appliance-detail-tt' },
  [ROUTE_URLS.APPLIANCES]: { helpTopic: 'com-appliances-tt' },
  [ROUTE_URLS.APPROVAL_POLICY]: { helpTopic: 'com-approval-tt' },
  [ROUTE_URLS.APPROVAL_REQUESTS]: { helpTopic: 'com-approval-tt' },
  [ROUTE_URLS.ARUBA_CENTRAL]: { helpTopic: 'com-aruba-tt' },
  [ROUTE_URLS.DSCC_INTEGRATION]: { helpTopic: 'com-dscc-tt' },
  [ROUTE_URLS.EMAIL_NOTIFICATION]: {
    helpTopic: 'com-email-notification-by-instance-tt',
  },
  [ROUTE_URLS.ERROR]: {
    helpTopic: 'com-error-tt',
    isUnauthenticated: true,
  },
  [ROUTE_URLS.FIRMWARE_ANALYSIS_SERVER_REPORT_ALL]: {
    helpTopic: DEFAULT_TOPIC,
    ldFlags: ['T_20260609_35862_FIRMWARE_ANALYSIS_REPORT_UI_BOOL'],
  },
  [ROUTE_URLS.FIRMWARE_ANALYSIS_SERVER_REPORT]: {
    helpTopic: DEFAULT_TOPIC,
    ldFlags: ['T_20260609_35862_FIRMWARE_ANALYSIS_REPORT_UI_BOOL'],
  },
  [ROUTE_URLS.FIRMWARE_ANALYSIS_REPORT]: {
    helpTopic: DEFAULT_TOPIC,
    ldFlags: ['T_20260609_35862_FIRMWARE_ANALYSIS_REPORT_UI_BOOL'],
  },
  [ROUTE_URLS.FIRMWARE_ANALYSIS_REPORT_ALL]: {
    helpTopic: DEFAULT_TOPIC,
    ldFlags: ['T_20260609_35862_FIRMWARE_ANALYSIS_REPORT_UI_BOOL'],
    exact: true,
  },
  [ROUTE_URLS.FIRMWARE_UPDATE_REPORT]: { helpTopic: 'com-firmware-report-tt' },
  [ROUTE_URLS.DEVICE_FIRMWARE_ANALYSIS_REPORT]: {
    helpTopic: 'com-firmware-report-tt',
    ldFlags: ['T_20260824_5226_FWU_REBOOT_OPTIONS_UI_BOOL'],
  },
  [ROUTE_URLS.DOWNLOAD_FIRMWARE_REPORT]: {
    helpTopic: 'com-firmware-report-tt',
  },
  [ROUTE_URLS.FIRMWARE]: { helpTopic: 'com-firmware-tt', exact: true },
  [ROUTE_URLS.SERVERS_FIRMWARE]: {
    helpTopic: 'com-firmware-tt',
    exact: true,
    ldFlags: ['T_20250106_98741_SUM_MODERNISATION_CFM_UI_BOOL'],
  },
  [ROUTE_URLS.GROUP_COMPLIANCE_ISSUE]: {
    helpTopic: 'com-group-compliance-issue-tt',
  },
  [ROUTE_URLS.GROUP_COMPLIANCE_ISSUES]: {
    helpTopic: 'com-group-compliance-issues-tt',
  },
  [ROUTE_URLS.GROUP_DETAIL]: { helpTopic: 'com-group-details-page-tt' },
  [ROUTE_URLS.GROUP_EXTERNAL_STORAGE_COMPLIANCE]: {
    helpTopic: 'com-group-compliance-storage-issue-tt',
  },
  [ROUTE_URLS.GROUP_ILO_DETAIL_ISSUE]: {
    helpTopic: 'com-group-compliance-ilo-issue-tt',
  },
  [ROUTE_URLS.GROUPS]: { helpTopic: 'com-groups-tt' },
  [ROUTE_URLS.HARDWARE_DETAIL]: { helpTopic: 'com-hwdetail-app-hw-tt' },
  [ROUTE_URLS.INVENTORY]: { helpTopic: 'com-inventory-tt' },
  [ROUTE_URLS.MANAGE]: { helpTopic: 'com-manage-tt' },
  [ROUTE_URLS.METRICS_DATA]: { helpTopic: 'com-metrics-dc-tt' },
  [ROUTE_URLS.OVERVIEW]: { helpTopic: 'com-overview-tt', mspSupported: true },
  [ROUTE_URLS.POLICY_DETAIL]: { helpTopic: DEFAULT_TOPIC },
  [ROUTE_URLS.REPORTS_CARBON_FOOTPRINT_DETAIL]: {
    helpTopic: 'com-sustainability-report-tt',
  },
  [ROUTE_URLS.REPORTS_SERVER_HARDWARE_INVENTORY_DETAIL]: {
    helpTopic: 'com-hardware-inventory-report-tt',
  },
  [ROUTE_URLS.REPORTS_SERVER_HARDWARE_INVENTORY]: {
    helpTopic: 'com-hardware-inventory-report-tt',
    ldFlags: ['T_20250709_89486_SERVER_HARDWARE_REPORT_NEW_UI_BOOL'],
    isSessionStorageEnabledForPage: true,
  },
  [ROUTE_URLS.REPORTS]: { helpTopic: 'com-analytics-tt', exact: true },
  [ROUTE_URLS.SERVER_DETAIL]: { helpTopic: 'com-serverdetail-tt' },
  [ROUTE_URLS.SERVER_PROFILE_DETAIL]: {
    helpTopic: DEFAULT_TOPIC,
    ldFlags: ['T_20230524_8648_SERVER_PROFILES_UI_BOOL'],
  },
  [ROUTE_URLS.SERVER_PROFILE]: {
    helpTopic: DEFAULT_TOPIC,
    exact: true,
    ldFlags: ['T_20230524_8648_SERVER_PROFILES_UI_BOOL'],
  },
  [ROUTE_URLS.SERVERS]: {
    helpTopic: 'com-servers-tt',
    isSessionStorageEnabledForPage: true,
  },
  [ROUTE_URLS.SERVICENOW]: { helpTopic: 'com-servicenow-tt' },
  [ROUTE_URLS.SETTING_DETAIL]: { helpTopic: 'com-setting-details-tt' },
  [ROUTE_URLS.SETTINGS]: { helpTopic: 'com-settings-tt' },
  [ROUTE_URLS.VCENTER_DETAIL]: {
    helpTopic: 'com-vcenter-details-tt',
  },
  [ROUTE_URLS.VSPHERE_INTEGRATION]: {
    helpTopic: 'com-vsphere-tt',
  },
  [ROUTE_URLS.HARDWARE_SECURITY_RISK_DETAILS]: {
    helpTopic: 'com-groups-tt',
    ldFlags: ['T_20250409_663_HARDWARE_ATTESTATION_UI_BOOL'],
  },
  [ROUTE_URLS.GROUP_HARDWARE_SECURITY_RISK]: {
    helpTopic: 'com-groups-tt',
    ldFlags: ['T_20250409_663_HARDWARE_ATTESTATION_UI_BOOL'],
  },
  [ROUTE_URLS.ALL_REGIONS_OVERVIEW]: {
    helpTopic: 'com-all-region-overview-tt',
    ldFlags: ['T_20260304_115135_ALL_REGIONS_UI_BOOL'],
  },
  [ROUTE_URLS.ALL_REGIONS_TENANTS]: {
    helpTopic: 'com-all-region-msp-tt',
    ldFlags: ['T_20260304_115135_ALL_REGIONS_UI_BOOL'],
    mspSupported: true,
  },
};

export default ROUTE_CONFIG;

// returns the path config only if:
// - it is marked as having complex LD flags (anything other than ||'ing all of the flags together)
// - there are no ld flags associated and not marked as having complex LD flags
// - all of the ld flags are enabled
export const getRouteConfig = (path, flags) => {
  const pathConfig = ROUTE_CONFIG?.[path];

  if (!pathConfig) return null;

  const { ldFlags = [], complexLDFlag } = pathConfig;

  if (complexLDFlag) return pathConfig;

  // the equivalent of ||'ing all of the flags together, stop as soon as one is true
  const isEnabled =
    ldFlags && ldFlags.length > 0
      ? ldFlags.some(
          flagName =>
            // eslint-disable-next-line indigo/flags-must-have-defaults
            !!flags?.[flagName]?.(),
        )
      : true;

  return isEnabled ? pathConfig : null;
};
